import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { CASE_STUDIES, CASE_LABELS } from "@/lib/caseStudies";
import { SERVICES } from "@/lib/i18n";

export default function CaseStudy() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const study = CASE_STUDIES[slug];

  if (!study) return <Navigate to="/clients" replace />;

  const c = study.content[lang] || study.content.en;
  const labels = CASE_LABELS[lang] || CASE_LABELS.en;
  const services = pickLang(SERVICES, lang);
  const stack = (study.servicesUsed || [])
    .map((s) => services.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <div data-testid="case-study-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 80% 20%, ${study.color}22 0%, transparent 50%), radial-gradient(circle at 15% 85%, ${study.color}18 0%, transparent 45%)`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-28">
          <Link
            to="/clients"
            data-testid="case-back"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={16} /> {labels.backToClients}
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline">{c.overline}</div>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] text-white">
                {c.headline}
              </h1>
              <p className="mt-6 md:mt-8 text-base md:text-xl text-slate-200 leading-relaxed max-w-2xl">
                {c.summary}
              </p>

              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3">
                {study.url && (
                  <a
                    href={study.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="case-visit-site"
                    className="btn-primary"
                  >
                    {labels.visitSite} <ArrowUpRight size={18} />
                  </a>
                )}
                <Link to="/demo" data-testid="case-cta-demo" className="btn-ghost">
                  {labels.bookDemo}
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-widest text-slate-400">
                <span>{study.industry}</span>
                <span>·</span>
                <span>{study.region}</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  className="absolute -inset-6 rounded-3xl opacity-30 blur-3xl"
                  style={{ background: study.color }}
                />
                <div
                  className="relative glass rounded-3xl aspect-[4/3] overflow-hidden p-8 md:p-10 flex items-center justify-center"
                  style={{
                    background: `radial-gradient(circle at 30% 20%, ${study.color}33 0%, transparent 55%), radial-gradient(circle at 75% 80%, ${study.color}22 0%, transparent 55%), linear-gradient(135deg, #1A2236 0%, #2A3B5C 100%)`,
                  }}
                >
                  <div className="absolute inset-0 bg-grid opacity-30" />
                  {study.logo && (
                    <img
                      src={study.logo}
                      alt={study.brand}
                      data-testid="case-brand-logo"
                      className="relative max-h-32 md:max-h-40 max-w-[75%] object-contain"
                      style={{ filter: `drop-shadow(0 0 32px ${study.color}66)` }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-14 md:py-20 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline">{c.results.title}</div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {c.results.metrics.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="glass rounded-2xl p-6 md:p-7"
                data-testid={`case-metric-${i}`}
              >
                <div
                  className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight"
                  style={{ color: study.color }}
                >
                  {m.value}
                </div>
                <div className="mt-3 text-xs md:text-sm text-slate-300 font-mono uppercase tracking-widest">
                  {m.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge */}
      <section className="py-16 md:py-24 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="overline">{c.challenge.title}</div>
          <div className="mt-8 space-y-5">
            {c.challenge.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-base md:text-lg text-slate-200 leading-relaxed"
                data-testid={`case-challenge-para-${i}`}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16 md:py-24 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="overline">{c.solution.title}</div>
          <div className="mt-8 space-y-5">
            {c.solution.paragraphs.map((p, i) => (
              <p
                key={i}
                className="text-base md:text-lg text-slate-200 leading-relaxed"
                data-testid={`case-solution-para-${i}`}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Results narrative */}
      {c.results.paragraphs && c.results.paragraphs.length > 0 && (
        <section className="py-16 md:py-24 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="overline">{c.results.title}</div>
            <div className="mt-8 space-y-5">
              {c.results.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-base md:text-lg text-slate-200 leading-relaxed"
                  data-testid={`case-results-para-${i}`}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services used */}
      {stack.length > 0 && (
        <section className="py-16 md:py-24 border-t border-white/10 bg-[#324567]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="overline">{labels.stackHeading}</div>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {stack.map((s, i) => (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`case-service-${s.slug}`}
                    className="card-hover glass rounded-2xl p-6 md:p-7 flex flex-col group h-full"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full gradient-bg flex items-center justify-center shrink-0">
                        <Check size={14} strokeWidth={3} className="text-black" />
                      </span>
                      <h3 className="font-display text-lg md:text-xl font-bold tracking-tight text-white group-hover:text-[#1FA8FF] transition-colors">
                        {s.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-sm text-slate-300 leading-relaxed line-clamp-3">
                      {s.summary}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {labels.ctaTitleA}{" "}
            <span className="gradient-text">{study.brand}</span>
            {labels.ctaTitleB}
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="case-bottom-cta-demo" className="btn-primary">
              {t("cta.demo_short")} <ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="case-bottom-cta-contact" className="btn-ghost">
              {t("cta.contact_sales")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
